import { dateRangeValidator, DATE_RANGE_MESSAGE, hasDateRangeError } from '../../../core/utils/date-range.validator';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormArray, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { CalendarModule } from 'primeng/calendar';
import { AccordionModule } from 'primeng/accordion';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CodesService } from '../../../core/services/codes/codes.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTriangleExclamation, faPlus, faCircle, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { AEAT_CODES } from '../../../core/constants/aeat-codes.constants';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';

export interface FiscalPeriod {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DigitalCertificate {
  representative: string;
  representativeNif: string;
  expiryDate: Date | null;
}

export interface FiscalGroupPeriod {
  startDate: Date | null;
  endDate: Date | null;
  taxGroupNumber: string;
  isDominantCompany: boolean;
  dominantCompanyName: string;
  dominantCompanyClientIds?: number[];
  dominantCompanyClientId?: number | null;
}

@Component({
  selector: 'app-fiscal-data-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule, InputTextareaModule, CheckboxModule, CalendarModule, AccordionModule, DropdownModule, MultiSelectModule, FontAwesomeModule, AutoAuditDirective],
  templateUrl: './fiscal-data-form.component.html',
  styleUrl: './fiscal-data-form.component.scss'
})
export class FiscalDataFormComponent implements OnChanges {
  @Input() clientId: any;
  @Input() clientType: string;
  @Input() snapshotData: any | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();
  @Output() certificateExpired = new EventEmitter<boolean>();
  @ViewChild('progressBar') progressBar: ElementRef;

  isSaved = false;
  faTriangleExclamation = faTriangleExclamation;
  faFloppyDisk = faFloppyDisk;
  representativeOptions: Array<{ id: number; name: string; nif?: string; documentNumber?: string }> = [];
  juridicClients: Array<{ id: number; name: string }> = [];
  faTrash = faTrash;
  faPlus = faPlus;
  faCircle = faCircle;
  aeatCodesOptions: any[] = AEAT_CODES;
  aeatCodesCombined: any[] = AEAT_CODES.map(code => ({
    label: `${code.codigo} - ${code.nombre}`,
    value: code.codigo,
    codigo: code.codigo,
    nombre: code.nombre
  }));
  hasCertificateExpired: boolean = false;

  fieldAuditData: LatestPerFieldMap = {};

  fiscalForm = this.fb.group({
    id: [],
    client: [],
    administrationCodeAEAT: [''],
    delegationAEAT: [''],
    aeatSelector: [''], // Combined selector for UI
    intraCommunityOperatorObligation: [false],
    intraCommunityOperatorPeriods: this.fb.array([]),
    codeLEI: [false],
    codeLEIPeriods: this.fb.array([]),
    codeEORI: [false],
    codeEORIPeriods: this.fb.array([]),
    presentationSIIObligation: [false],
    presentationSIIPeriods: this.fb.array([]),
    isBigCompany: [false],
    isBigCompanyPeriods: this.fb.array([]),
    paymentOnAccountIS403LIS: [false],
    paymentOnAccountIS403LISPeriods: this.fb.array([]),
    isFiscalGroup: [false],
    fiscalGroupPeriods: this.fb.array([]),
    digitalCertificate: [false], // Checkbox for physical persons
    digitalCertificates: this.fb.array([]),
    socialCollaboratorAuthorization: [false],
    observations: ['']
  });

  get roiPeriods(): FormArray {
    return this.fiscalForm.get('intraCommunityOperatorPeriods') as FormArray;
  }

  get leiPeriods(): FormArray {
    return this.fiscalForm.get('codeLEIPeriods') as FormArray;
  }

  get eoriPeriods(): FormArray {
    return this.fiscalForm.get('codeEORIPeriods') as FormArray;
  }

  get siiPeriods(): FormArray {
    return this.fiscalForm.get('presentationSIIPeriods') as FormArray;
  }

  get bigCompanyPeriods(): FormArray {
    return this.fiscalForm.get('isBigCompanyPeriods') as FormArray;
  }

  get is403LISPeriods(): FormArray {
    return this.fiscalForm.get('paymentOnAccountIS403LISPeriods') as FormArray;
  }

  get fiscalGroupPeriods(): FormArray {
    return this.fiscalForm.get('fiscalGroupPeriods') as FormArray;
  }

  get digitalCertificates(): FormArray {
    return this.fiscalForm.get('digitalCertificates') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private codesService: CodesService,
    private renderer: Renderer2,
    private auditService: AuditService,
  ) {
    this.fiscalForm.valueChanges.subscribe(() => {
      if (this.fiscalForm.dirty) {
        this.formIsDirty.emit(true);
      }
    });

    // Watch for changes in digital certificates
    this.digitalCertificates.valueChanges.subscribe(() => {
      this.checkCertificateExpiration();
    });

    // Add first period when checkbox is activated
    this.fiscalForm.get('intraCommunityOperatorObligation')?.valueChanges.subscribe((value) => {
      if (value && this.roiPeriods.length === 0) {
        this.addRoiPeriod();
      }
    });

    this.fiscalForm.get('codeLEI')?.valueChanges.subscribe((value) => {
      if (value && this.leiPeriods.length === 0) {
        this.addLeiPeriod();
      }
    });

    this.fiscalForm.get('codeEORI')?.valueChanges.subscribe((value) => {
      if (value && this.eoriPeriods.length === 0) {
        this.addEoriPeriod();
      }
    });

    this.fiscalForm.get('presentationSIIObligation')?.valueChanges.subscribe((value) => {
      if (value && this.siiPeriods.length === 0) {
        this.addSiiPeriod();
      }
    });

    this.fiscalForm.get('paymentOnAccountIS403LIS')?.valueChanges.subscribe((value) => {
      if (value && this.is403LISPeriods.length === 0) {
        this.addIS403LISPeriod();
      }
    });

    this.fiscalForm.get('isBigCompany')?.valueChanges.subscribe((value) => {
      if (value && this.bigCompanyPeriods.length === 0) {
        this.addBigCompanyPeriod();
      }
    });

    this.fiscalForm.get('isFiscalGroup')?.valueChanges.subscribe((value) => {
      if (value && this.fiscalGroupPeriods.length === 0) {
        this.addFiscalGroupPeriod();
      }
    });

    // Watch for digital certificate checkbox (for physical persons)
    this.fiscalForm.get('digitalCertificate')?.valueChanges.subscribe((value) => {
      if (this.isPhysicalPerson) {
        if (value && this.digitalCertificates.length === 0) {
          this.addCertificate();
        } else if (!value && this.digitalCertificates.length > 0) {
          while (this.digitalCertificates.length > 0) {
            this.digitalCertificates.removeAt(0);
          }
        }
      }
    });

    // When combined AEAT selector changes, update code and name fields
    this.fiscalForm.get('aeatSelector')?.valueChanges.subscribe((codigo) => {
      if (codigo) {
        const aeatCode = AEAT_CODES.find(c => c.codigo === codigo);
        if (aeatCode) {
          this.fiscalForm.patchValue({ 
            administrationCodeAEAT: aeatCode.codigo,
            delegationAEAT: aeatCode.nombre
          }, { emitEvent: false });
        }
      }
    });
  }

  createPeriodFormGroup(period?: FiscalPeriod) {
    return this.fb.group({
      startDate: [period?.startDate ? new Date(period.startDate) : null],
      endDate: [period?.endDate ? new Date(period.endDate) : null]
    }, { validators: dateRangeValidator() });
  }

  createCertificateFormGroup(certificate?: DigitalCertificate & { id?: number }) {
    return this.fb.group({
      id: [certificate?.id || null],
      representativeId: [null as number | null],
      representative: [certificate?.representative || ''],
      representativeNif: [certificate?.representativeNif || ''],
      expiryDate: [parseDateOnlyToLocal(certificate?.expiryDate)]
    });
  }

  onRepresentativeSelect(index: number, clientId: number | null): void {
    const client = this.representativeOptions.find(c => c.id === clientId);
    const certGroup = this.digitalCertificates.at(index) as FormGroup;
    certGroup.patchValue({
      representative: client?.name || '',
      representativeNif: client?.nif || (client as any)?.documentNumber || ''
    }, { emitEvent: false });
  }

  addRoiPeriod() {
    this.roiPeriods.push(this.createPeriodFormGroup());
    this.formIsDirty.emit(true);
  }

  removeRoiPeriod(index: number) {
    this.roiPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  addLeiPeriod() {
    this.leiPeriods.push(this.createPeriodFormGroup());
    this.formIsDirty.emit(true);
  }

  removeLeiPeriod(index: number) {
    this.leiPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  addEoriPeriod() {
    this.eoriPeriods.push(this.createPeriodFormGroup());
    this.formIsDirty.emit(true);
  }

  removeEoriPeriod(index: number) {
    this.eoriPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  addSiiPeriod() {
    this.siiPeriods.push(this.createPeriodFormGroup());
    this.formIsDirty.emit(true);
  }

  removeSiiPeriod(index: number) {
    this.siiPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  addIS403LISPeriod() {
    this.is403LISPeriods.push(this.createPeriodFormGroup());
    this.formIsDirty.emit(true);
  }

  removeIS403LISPeriod(index: number) {
    this.is403LISPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  addBigCompanyPeriod(startDate?: Date | null) {
    const group = this.createPeriodFormGroup(startDate ? { startDate, endDate: null } : undefined);
    group.get('startDate')?.valueChanges.subscribe((date) => {
      this.syncIS403LISFromBigCompany(date);
    });
    this.bigCompanyPeriods.push(group);
    this.formIsDirty.emit(true);
  }

  removeBigCompanyPeriod(index: number) {
    this.bigCompanyPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  createFiscalGroupPeriodFormGroup(period?: FiscalGroupPeriod) {
    return this.fb.group({
      startDate: [period?.startDate ? new Date(period.startDate) : null],
      endDate: [period?.endDate ? new Date(period.endDate) : null],
      taxGroupNumber: [period?.taxGroupNumber || ''],
      isDominantCompany: [period?.isDominantCompany ?? false],
      dominantCompanyName: [period?.dominantCompanyName || ''],
      dominantCompanyClientIds: [period?.dominantCompanyClientIds ?? []],
      dominantCompanyClientId: [period?.dominantCompanyClientId ?? this.resolveDominantCompanyClientId(period)],
    }, { validators: dateRangeValidator() });
  }

  private resolveDominantCompanyClientId(period?: FiscalGroupPeriod): number | null {
    if (period?.dominantCompanyClientId != null) {
      return period.dominantCompanyClientId;
    }
    if (period?.dominantCompanyName) {
      const match = this.juridicClients.find((client) => client.name === period.dominantCompanyName);
      return match?.id ?? null;
    }
    return null;
  }

  private wireDominantCompanyExclusiveLogic(group: FormGroup): void {
    group.get('isDominantCompany')?.valueChanges.subscribe((isDominant) => {
      if (isDominant) {
        // Por identidad y no por posición: al borrar una fila anterior las posiciones cambian.
        this.fiscalGroupPeriods.controls.forEach((control) => {
          if (control !== group) {
            control.get('isDominantCompany')?.setValue(false, { emitEvent: false });
            control.get('dominantCompanyClientIds')?.setValue([], { emitEvent: false });
            control.get('dominantCompanyName')?.setValue('', { emitEvent: false });
            control.get('dominantCompanyClientId')?.setValue(null, { emitEvent: false });
          }
        });
        group.get('dominantCompanyClientId')?.setValue(null, { emitEvent: false });
      } else {
        group.get('dominantCompanyClientIds')?.setValue([], { emitEvent: false });
        group.get('dominantCompanyName')?.setValue('', { emitEvent: false });
        group.get('dominantCompanyClientId')?.setValue(null, { emitEvent: false });
      }
      this.formIsDirty.emit(true);
    });
  }

  private pushFiscalGroupPeriod(period?: FiscalGroupPeriod, markDirty = true): void {
    const group = this.createFiscalGroupPeriodFormGroup(period);
    this.fiscalGroupPeriods.push(group);
    this.wireDominantCompanyExclusiveLogic(group);
    if (markDirty) {
      this.formIsDirty.emit(true);
    }
  }

  onDominantCompanyClientSelect(periodIndex: number, clientId: number | null): void {
    const client = this.juridicClients.find((c) => c.id === clientId);
    this.fiscalGroupPeriods.at(periodIndex)?.patchValue(
      { dominantCompanyName: client?.name ?? '' },
      { emitEvent: false },
    );
    this.formIsDirty.emit(true);
  }

  getSelectedDominantClients(periodIndex: number): Array<{ id: number; name: string }> {
    const selectedIds = this.fiscalGroupPeriods.at(periodIndex)?.get('dominantCompanyClientIds')?.value || [];
    return Array.isArray(selectedIds)
      ? this.juridicClients.filter((client) => selectedIds.includes(client.id))
      : [];
  }

  addFiscalGroupPeriod() {
    this.pushFiscalGroupPeriod();
  }

  removeFiscalGroupPeriod(index: number) {
    this.fiscalGroupPeriods.removeAt(index);
    this.formIsDirty.emit(true);
  }

  private syncIS403LISFromBigCompany(startDate: Date | null) {
    if (!startDate) return;
    this.fiscalForm.get('paymentOnAccountIS403LIS')?.setValue(true, { emitEvent: false });
    if (this.is403LISPeriods.length === 0) {
      this.is403LISPeriods.push(this.createPeriodFormGroup({ startDate, endDate: null }));
    } else {
      const firstPeriod = this.is403LISPeriods.at(0);
      if (!firstPeriod.get('startDate')?.value) {
        firstPeriod.get('startDate')?.setValue(startDate, { emitEvent: false });
      }
    }
    this.formIsDirty.emit(true);
  }

  addCertificate() {
    // Para persona física, solo permitir un certificado
    if (this.clientType === 'PHYSICAL_PERSON' && this.digitalCertificates.length >= 1) {
      return;
    }
    this.digitalCertificates.push(this.createCertificateFormGroup());
    this.formIsDirty.emit(true);
  }
  
  get isPhysicalPerson(): boolean {
    return this.clientType === 'PHYSICAL_PERSON';
  }

  removeCertificate(index: number) {
    this.digitalCertificates.removeAt(index);
    this.formIsDirty.emit(true);
  }

  async ngOnInit() {
    if (this.clientId && !this.snapshotData) {
      await this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.fiscalForm.patchValue(this.snapshotData, { emitEvent: false });
        this.fiscalForm.disable({ emitEvent: false });
      } else if (!changes['snapshotData'].firstChange) {
        this.fiscalForm.enable({ emitEvent: false });
        void this.loadData();
      }
    }
  }

  private async loadData() {
    if (!this.clientId) return;
    const [data, otherData, clients, juridicClients] = await Promise.all([
      this.clientsService.getClientFiscalData(this.clientId),
      this.clientsService.getClientOtherData(this.clientId),
      this.clientsService.getClientsNames().catch(() => []),
      this.clientsService.getClientsNames('JURIDIC_PERSON').catch(() => []),
    ]);
    this.representativeOptions = clients || [];
    this.juridicClients = juridicClients || [];
    if (data) {
      // Clear FormArrays
      while (this.roiPeriods.length) this.roiPeriods.removeAt(0);
      while (this.leiPeriods.length) this.leiPeriods.removeAt(0);
      while (this.eoriPeriods.length) this.eoriPeriods.removeAt(0);
      while (this.siiPeriods.length) this.siiPeriods.removeAt(0);
      while (this.bigCompanyPeriods.length) this.bigCompanyPeriods.removeAt(0);
      while (this.is403LISPeriods.length) this.is403LISPeriods.removeAt(0);
      while (this.fiscalGroupPeriods.length) this.fiscalGroupPeriods.removeAt(0);
      while (this.digitalCertificates.length) this.digitalCertificates.removeAt(0);

      if (data.intraCommunityOperatorPeriods?.length > 0) {
        data.intraCommunityOperatorPeriods.forEach((period: FiscalPeriod) => {
          this.roiPeriods.push(this.createPeriodFormGroup(period));
        });
      }
      if (otherData?.codeLEIPeriods?.length > 0) {
        otherData.codeLEIPeriods.forEach((period: FiscalPeriod) => {
          this.leiPeriods.push(this.createPeriodFormGroup(period));
        });
      }
      if (data.codeEORIPeriods?.length > 0) {
        data.codeEORIPeriods.forEach((period: FiscalPeriod) => {
          this.eoriPeriods.push(this.createPeriodFormGroup(period));
        });
      }
      if (data.presentationSIIPeriods?.length > 0) {
        data.presentationSIIPeriods.forEach((period: FiscalPeriod) => {
          this.siiPeriods.push(this.createPeriodFormGroup(period));
        });
      }
      if (data.isBigCompanyPeriods?.length > 0) {
        data.isBigCompanyPeriods.forEach((period: FiscalPeriod) => {
          const group = this.createPeriodFormGroup(period);
          group.get('startDate')?.valueChanges.subscribe((date) => {
            this.syncIS403LISFromBigCompany(date);
          });
          this.bigCompanyPeriods.push(group);
        });
      }
      if (data.paymentOnAccountIS403LISPeriods?.length > 0) {
        data.paymentOnAccountIS403LISPeriods.forEach((period: FiscalPeriod) => {
          this.is403LISPeriods.push(this.createPeriodFormGroup(period));
        });
      }
      if (data.fiscalGroupPeriods?.length > 0) {
        data.fiscalGroupPeriods.forEach((period: FiscalGroupPeriod) => {
          this.pushFiscalGroupPeriod(period, false);
        });
      }
      if (data.digitalCertificates?.length > 0) {
        data.digitalCertificates.forEach((cert: any) => {
          const certGroup = this.createCertificateFormGroup({
            id: cert.id,
            representative: cert.representativeName || cert.representative || '',
            representativeNif: cert.representativeNif || '',
            expiryDate: cert.expiryDate,
          });
          if (cert.representativeId) {
            certGroup.patchValue({ representativeId: cert.representativeId }, { emitEvent: false });
          }
          this.digitalCertificates.push(certGroup);
        });
      }

      let aeatSelector = '';
      if (data.administrationCodeAEAT) {
        aeatSelector = data.administrationCodeAEAT;
      } else if (data.delegationAEAT) {
        const aeatByName = AEAT_CODES.find(c => c.nombre === data.delegationAEAT);
        if (aeatByName) aeatSelector = aeatByName.codigo;
      }

      this.fiscalForm.patchValue({
        id: data.id,
        administrationCodeAEAT: data.administrationCodeAEAT,
        delegationAEAT: data.delegationAEAT,
        aeatSelector,
        intraCommunityOperatorObligation: data.intraCommunityOperatorObligation,
        codeLEI: otherData?.codeLEI || false,
        codeEORI: data.codeEORI,
        presentationSIIObligation: data.presentationSIIObligation,
        isBigCompany: data.isBigCompany || false,
        paymentOnAccountIS403LIS: data.paymentOnAccountIS403LIS || false,
        isFiscalGroup: data.isFiscalGroup || false,
        digitalCertificate: this.isPhysicalPerson && data.digitalCertificates && data.digitalCertificates.length > 0,
        socialCollaboratorAuthorization: data.socialCollaboratorAuthorization,
        observations: data.observations || '',
      });

      setTimeout(() => { this.checkCertificateExpiration(); }, 0);
    }

    try {
      this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_fiscal_data');
    } catch {
      this.fieldAuditData = {};
    }
  }

  dateRangeError = '';
  isSavingFiscal = false;

  async save() {
    if (this.isSavingFiscal) return;
    this.dateRangeError = hasDateRangeError(this.fiscalForm) ? DATE_RANGE_MESSAGE : '';
    if (this.dateRangeError) return; // un periodo con el fin antes del inicio no se guarda // doble clic = dos filas fiscales para el mismo cliente
    this.isSavingFiscal = true;
    try {
      await this.saveFiscalData();
    } finally {
      this.isSavingFiscal = false;
    }
  }

  private async saveFiscalData() {
    const { aeatSelector, codeLEI, codeLEIPeriods, digitalCertificate, ...fiscalData } = this.fiscalForm.value;
    fiscalData.client = Number(this.clientId);
    if (!fiscalData.id) {
      delete fiscalData['id'];
    }
    if (Array.isArray(fiscalData.digitalCertificates)) {
      fiscalData.digitalCertificates = fiscalData.digitalCertificates.map(
        ({ representativeId, representative, representativeNif, ...cert }: any) => ({
          ...cert,
          representativeId: representativeId ?? null,
          expiryDate: toDateOnlyPayload(cert.expiryDate),
        })
      );
    }
    const savedFiscal = await this.clientsService.saveClientFiscalData(fiscalData);
    // Si lo siguiente falla, el reintento debe actualizar esta fila y no crear otra.
    if (savedFiscal?.id && !this.fiscalForm.get('id')?.value) {
      this.fiscalForm.patchValue({ id: savedFiscal.id }, { emitEvent: false });
    }

    // Save LEI data to otherData
    const otherData = await this.clientsService.getClientOtherData(this.clientId);
    const otherDataPayload = {
      ...otherData,
      codeLEI: codeLEI,
      codeLEIPeriods: codeLEIPeriods,
      client: Number(this.clientId)
    };
    if (!otherDataPayload.id) {
      delete otherDataPayload['id'];
    }
    await this.clientsService.saveClientOtherData(otherDataPayload);

    await this.loadData();
    this.checkCertificateExpiration();
    this.isSaved = true;
    let width = 0;
    const interval = setInterval(() => {
      if (width >= 100) {
        clearInterval(interval);
        this.isSaved = false;
      } else {
        width++;
        this.renderer.setProperty(this.progressBar.nativeElement, 'value', width);
      }
    }, 30);
  }

  checkCertificateExpiration() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.hasCertificateExpired = this.digitalCertificates.controls.some(control => {
      const expiryDate = control.get('expiryDate')?.value;
      if (expiryDate) {
        const expiry = new Date(expiryDate);
        expiry.setHours(0, 0, 0, 0);
        return expiry < today;
      }
      return false;
    });
    
    this.certificateExpired.emit(this.hasCertificateExpired);
  }

  isCertificateExpired(index: number): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const expiryDate = this.digitalCertificates.at(index).get('expiryDate')?.value;
    if (expiryDate) {
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    }
    return false;
  }

  hasActivePeriod(periods: FormArray): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return periods.controls.some(control => {
      const startDate = control.get('startDate')?.value;
      const endDate = control.get('endDate')?.value;

      if (!startDate) return false;

      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      // El periodo debe haber comenzado
      if (start > today) return false;

      // Si no hay fecha de baja, está activo
      if (!endDate) return true;

      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);

      // Si la fecha de baja es en el futuro, está activo
      return end >= today;
    });
  }

  get isRoiActive(): boolean {
    return this.fiscalForm.get('intraCommunityOperatorObligation')?.value && 
           this.hasActivePeriod(this.roiPeriods);
  }

  get isLeiActive(): boolean {
    return this.fiscalForm.get('codeLEI')?.value && 
           this.hasActivePeriod(this.leiPeriods);
  }

  get isEoriActive(): boolean {
    return this.fiscalForm.get('codeEORI')?.value && 
           this.hasActivePeriod(this.eoriPeriods);
  }

  get isSiiActive(): boolean {
    return this.fiscalForm.get('presentationSIIObligation')?.value && 
           this.hasActivePeriod(this.siiPeriods);
  }

  get isIS403LISActive(): boolean {
    return this.fiscalForm.get('paymentOnAccountIS403LIS')?.value && 
           this.hasActivePeriod(this.is403LISPeriods);
  }

  get isBigCompanyActive(): boolean {
    return this.fiscalForm.get('isBigCompany')?.value &&
           this.hasActivePeriod(this.bigCompanyPeriods);
  }

  get isFiscalGroupActive(): boolean {
    return this.fiscalForm.get('isFiscalGroup')?.value &&
           this.hasActivePeriod(this.fiscalGroupPeriods);
  }
}


